import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import { makeStyles } from "@material-ui/core/styles";
import CardTitle from "@saleor/components/CardTitle";
import { OrderDetailsListColumns } from "@saleor/config";
import useBulkActions from "@saleor/hooks/useBulkActions";
import { maybe } from "@saleor/misc";
import { ListProps } from "@saleor/types";
import {
  OrderLineStatusEnum,
  OrderVerticalType,
  SmopOrderStatusEnum,
  SmopUserTypeCode
} from "@saleor/types/globalTypes";
import classNames from "classnames";
import React from "react";
import { FormattedMessage } from "react-intl";

import { StoreOrderDetails_storeOrder } from "../../types/StoreOrderDetails";
import TableEdit from "./components/TableEdit";
import TableLines from "./components/TableLines";

const useStyles = makeStyles(
  theme => ({
    buttonOrange: {
      color: "#E26139 !important"
    },
    buttonPicked: {
      color: "#6DAB64 !important"
    },
    buttonPicking: {
      color: "#3D3D3D !important"
    },
    clickableRow: {
      cursor: "pointer"
    },
    colCheckBox: {
      paddingLeft: "12px !important"
    },
    colEditName: {
      width: "90% !important"
    },
    colName: {
      paddingLeft: "0px",
      width: "60% !important"
    },
    colNameLabel: {
      marginLeft: "0px"
    },
    colPrice: {
      width: "10% !important"
    },
    colQuantity: {
      width: "8%"
    },
    colStatus: {
      width: "10% !important"
    },
    colStatusCell: {
      whiteSpace: "nowrap",
      width: "10% !important"
    },
    colTotal: {
      width: "10% !important"
    },
    disabledSave: {
      color: "#a7a9ab !important"
    },
    editQty: {
      width: "10% !important"
    },
    editQtyInput: {
      width: "5em"
    },
    editQtyMarginTop: {
      marginTop: "4px"
    },
    infoLabel: {
      display: "inline-block"
    },
    infoLabelWithMargin: {
      marginBottom: theme.spacing()
    },
    infoRow: {
      padding: theme.spacing(2, 3)
    },
    orderNumber: {
      display: "inline",
      marginLeft: theme.spacing(1)
    },
    prodCheckbox: {
      "& svg": {
        "& rect": {
          fill: "#e5e5e5"
        }
      }
    },
    statusBar: {
      paddingTop: 0
    },
    table: {
      overflowX: "auto",
      width: "100%"
    },
    tableResponsive: {
      overflowX: "auto"
    },
    tableRow: {
      cursor: "pointer"
    },
    tableRow1: {
      borderBottom: "0px",
      cursor: "pointer"
    },
    tableRow2: {
      borderTop: "0px",
      cursor: "pointer",
      fontSize: "14px !important",
      padding: "0px",
      paddingRight: "50px",
      textAlign: "left",
      verticalAlign: "top",
      width: "400px"
    },

    toolbarPick: {
      display: "inline-flex"
    }
  }),
  { name: "StoreOrderDetailsList" }
);

enum paymentEnum {
  CARD = "card",
  CASH = "cash",
  GCASH = "gcash"
}

export interface StoreOrderDetailsListProps
  extends ListProps<OrderDetailsListColumns> {
  storeOrder: StoreOrderDetails_storeOrder;
  orderStatus: string;
  addProductDialog: (data: any) => void;
  editProductDialog: (data: { type: string; payload: any }) => void;
  userType: string;
  onSubmitOrderLineStatus: (lineIds: any, status: OrderLineStatusEnum) => void;
  disableAction: boolean;
}

const StoreOrderDetailsList: React.FC<StoreOrderDetailsListProps> = props => {
  const {
    addProductDialog,
    storeOrder,
    onRowClick,
    orderStatus,
    editProductDialog,
    userType,
    onSubmitOrderLineStatus,
    disableAction
  } = props;
  const classes = useStyles(props);

  const {
    isSelected: isChecked,
    listElements,
    reset,
    toggle,
    toggleAll
  } = useBulkActions([]);

  const [editMode, setEditMode] = React.useState(false);
  const [lines, setLines] = React.useState([]);

  React.useEffect(() => {
    setLines([]);
    reset();
    setEditMode(false);
  }, [storeOrder && storeOrder.lines]);

  React.useEffect(() => {
    setLines([]);
    reset();
  }, [editMode]);

  const checkNotEditable = () => {
    switch (userType) {
      case SmopUserTypeCode.STORE_ADMIN:
        if (maybe(() => orderStatus) !== SmopOrderStatusEnum.ACKNOWLEDGED) {
          return true;
        } else {
          return false;
        }
      default:
        return true;
    }
  };
  const onChangeQuantity = (newQuantity, orderLineId) => {
    const temp_object = {
      lineId: orderLineId,
      quantity: newQuantity
    };

    const checkIfExist = () =>
      lines.some(({ lineId }) => lineId === orderLineId);

    if (lines.length === 0) {
      setLines([temp_object]);
    } else {
      if (checkIfExist()) {
        const temp_lines = [...lines];
        const index = temp_lines.findIndex(i => i.lineId === orderLineId);
        temp_lines[index] = { ...temp_lines[index] };
        temp_lines[index].quantity = newQuantity;
        temp_lines[index].lineId = orderLineId;
        setLines(temp_lines);
      } else {
        setLines([...lines, temp_object]);
      }
    }
  };

  return (
    <Card>
      <CardTitle
        title="Order Details"
        toolbar={
          checkNotEditable() === false &&
          (storeOrder.order.verticalType ===
            OrderVerticalType.GENERAL_MERCHANDISE &&
            (storeOrder.order.payments[0].paymentMethodType ===
              paymentEnum.CARD ||
              storeOrder.order.payments[0].paymentMethodType ===
                paymentEnum.GCASH)) === false ? (
            editMode ? (
              <>
                <Button
                  id="addProductDialogBtn"
                  variant="text"
                  color="primary"
                  onClick={() =>
                    addProductDialog({ payload: "sss", type: "ADD_PRODUCT" })
                  }
                >
                  <FormattedMessage defaultMessage="ADD" description="button" />
                </Button>
                <Button
                  id="cancelEditModeBtn"
                  variant="text"
                  color="primary"
                  onClick={() => setEditMode(false)}
                >
                  <FormattedMessage
                    defaultMessage="CANCEL"
                    description="button"
                  />
                </Button>
                <Button
                  id="editProductQtyBtn"
                  disabled={lines.length === 0 ? true : false}
                  variant="text"
                  color="primary"
                  onClick={() =>
                    editProductDialog({
                      payload: lines,
                      type: "UPDATE_QTY"
                    })
                  }
                >
                  <span
                    className={classNames({
                      [classes.disabledSave]: lines.length === 0
                    })}
                  >
                    SAVE
                  </span>
                </Button>
              </>
            ) : (
              <Button
                id="enableEditModeBtn"
                variant="text"
                color={disableAction ? "default" : "primary"}
                onClick={() => setEditMode(true)}
                disabled={disableAction}
              >
                <FormattedMessage defaultMessage="Edit" description="button" />
              </Button>
            )
          ) : null
        }
      />
      {editMode ? (
        <TableEdit
          storeOrder={storeOrder}
          checkNotEditable={checkNotEditable}
          classes={classes}
          editProductDialog={editProductDialog}
          isChecked={isChecked}
          listElements={listElements}
          onChangeQuantity={onChangeQuantity}
          toggle={toggle}
          toggleAll={toggleAll}
        />
      ) : (
        <TableLines
          storeOrder={storeOrder}
          checkNotEditable={checkNotEditable() || disableAction}
          classes={classes}
          editProductDialog={editProductDialog}
          isChecked={isChecked}
          listElements={listElements}
          onRowClick={onRowClick}
          onSubmitOrderLineStatus={onSubmitOrderLineStatus}
          orderStatus={orderStatus}
          reset={reset}
          toggle={toggle}
          toggleAll={toggleAll}
        />
      )}
    </Card>
  );
};
StoreOrderDetailsList.displayName = "StoreOrderDetailsList";
export default StoreOrderDetailsList;
